/**
 * ProductIntegrationsPanel — the per-product `/<product>/settings/integrations`
 * body. Lists the integration providers a single product consumes, each with
 * its connection count + status, and links out to the Integrations hub (which
 * owns the connect flow, catalog, and per-connection management). Purely
 * presentational; the settings page resolves the rows server-side.
 *
 * See docs/integrations/OPERABILITY-PRD.md §7.
 */

import { RouterLink as NextLink } from "../../router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../card";
import { Badge } from "../../badge";
import { Link } from "../link";

export interface ProductIntegrationProviderRow {
  slug: string;
  label: string;
  /** "available" | "configured" | etc. */
  status: string;
  authMode?: string;
  connectionCount: number;
}

export interface ProductIntegrationsPanelProps {
  /** Human product name, e.g. "CRM". */
  productLabel: string;
  providers: ProductIntegrationProviderRow[];
  /** Hub links (defaults to the integrations hub). */
  catalogHref?: string;
  connectionsHref?: string;
}

export function ProductIntegrationsPanel({
  productLabel,
  providers,
  catalogHref = "/settings/integrations",
  connectionsHref = "/settings/integrations",
}: ProductIntegrationsPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Integrations</CardTitle>
        <CardDescription>
          Providers {productLabel} can pull from and push to. Connecting and per-connection
          settings live in the <Link href={catalogHref}>Integrations hub</Link>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {providers.length === 0 ? (
          <p className="type-body text-muted-foreground">
            No providers for {productLabel} yet. <Link href={catalogHref}>Browse the catalog</Link>.
          </p>
        ) : (
          <div>
            {providers.map((p) => (
              <div
                key={p.slug}
                className="flex items-center justify-between border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0"
              >
                <div className="flex items-center gap-2">
                  <NextLink href={`${connectionsHref}`} className="type-body font-medium hover:underline">
                    {p.label}
                  </NextLink>
                  {p.authMode === "api_key" && (
                    <Badge variant="outline" className="font-normal text-muted-foreground">API key</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 type-body text-muted-foreground">
                  <span>
                    {p.connectionCount} {p.connectionCount === 1 ? "connection" : "connections"}
                  </span>
                  <Badge variant={p.connectionCount > 0 ? "default" : "outline"}>
                    {p.connectionCount > 0 ? "connected" : p.status === "configured" ? "ready" : "available"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
